export type ProviderCreditKind =
  | "unavailable"
  | "applied_only"
  | "estimated_remaining"
  | "authoritative_remaining";

export type ProviderCreditDataState =
  | "not_configured"
  | "syncing"
  | "backfilling"
  | "current"
  | "delayed"
  | "stale"
  | "partial"
  | "error"
  | "disabled";

export type ProviderCreditVerificationLevel =
  | "owner_asserted"
  | "platform_verified"
  | "provider_authoritative";

export type ProviderCreditCoverage = {
  start_at?: string | null;
  end_at?: string | null;
  complete_for_grant?: boolean;
  known_gap_count?: number;
};

/**
 * Financial values use decimal strings containing integer micros. `null`
 * means unknown and must never be normalized to zero.
 */
export type ProviderCreditStatus = {
  kind: ProviderCreditKind;
  data_state: ProviderCreditDataState;
  verification_level?: ProviderCreditVerificationLevel | null;
  remaining_amount_micros: string | null;
  applied_promotion_micros: string | null;
  unattributed_promotion_micros: string | null;
  currency?: string | null;
  source?: string | null;
  has_export_rows?: boolean | null;
  promotion_row_count?: string | null;
  coverage?: ProviderCreditCoverage | null;
  as_of_at?: string | null;
  data_through_export_at?: string | null;
  stale_after_at?: string | null;
  reason_codes?: string[];
  console_url?: string | null;
};

export type ProviderCreditTone =
  | "neutral"
  | "info"
  | "positive"
  | "warning"
  | "danger";

export type ProviderCreditPresentation = {
  badge: string;
  tone: ProviderCreditTone;
  primary: string;
  secondary: string;
  details: string[];
  action: "setup" | "details" | "retry" | "google";
  consoleURL: string;
};

const GOOGLE_CREDITS_URL = "https://console.cloud.google.com/billing/credits";
const INTEGER_MICROS = /^-?\d+$/;

export function providerCreditSourceCanSave({
  loaded,
  loadError,
  sourceConfigured,
  sourceVersion,
}: {
  loaded: boolean;
  loadError: boolean;
  sourceConfigured: boolean;
  sourceVersion?: number | null;
}) {
  if (!loaded || loadError) return false;
  if (!sourceConfigured) return true;
  return (
    typeof sourceVersion === "number" &&
    Number.isSafeInteger(sourceVersion) &&
    sourceVersion > 0
  );
}

function safeCurrency(value?: string | null) {
  const currency = String(value || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(currency) ? currency : "USD";
}

export function parseMicros(value?: string | number | null): bigint | null {
  if (typeof value === "number") {
    if (!Number.isSafeInteger(value)) return null;
    return BigInt(value);
  }
  const text = String(value ?? "").trim();
  if (!INTEGER_MICROS.test(text)) return null;
  try {
    return BigInt(text);
  } catch {
    return null;
  }
}

export function decimalCurrencyToMicros(value: string) {
  const text = String(value || "").trim();
  if (!/^\d+(?:\.\d{0,6})?$/.test(text)) return null;
  const [whole, fraction = ""] = text.split(".");
  try {
    return (
      BigInt(whole) * 1_000_000n +
      BigInt(fraction.padEnd(6, "0"))
    ).toString();
  } catch {
    return null;
  }
}

function currencyFractionDigits(currency: string, locale?: string) {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

/**
 * Formats integer micros without converting the amount through JavaScript
 * `number`, preserving exact int64 values.
 */
export function formatProviderCreditMicros(
  value?: string | null,
  currencyValue?: string | null,
  locale?: string,
) {
  const micros = parseMicros(value);
  if (micros === null) return "—";

  const currency = safeCurrency(currencyValue);
  const fractionDigits = Math.min(
    6,
    Math.max(0, currencyFractionDigits(currency, locale)),
  );
  const negative = micros < 0n;
  const absolute = negative ? -micros : micros;
  const displayScale = 10n ** BigInt(fractionDigits);
  const microsPerDisplayUnit = 10n ** BigInt(6 - fractionDigits);
  const roundedDisplayUnits =
    (absolute + microsPerDisplayUnit / 2n) / microsPerDisplayUnit;
  const whole = roundedDisplayUnits / displayScale;
  const fraction = roundedDisplayUnits % displayScale;

  let groupedWhole = whole.toString();
  try {
    groupedWhole = new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
      useGrouping: true,
    }).format(whole);
  } catch {
    // The exact ungrouped integer is still safe and readable.
  }

  const decimal =
    fractionDigits > 0
      ? `.${fraction.toString().padStart(fractionDigits, "0")}`
      : "";
  return `${currency} ${negative ? "-" : ""}${groupedWhole}${decimal}`;
}

export function isGoogleProviderSlug(slug?: string | null) {
  return ["gcp", "google_cloud", "googlecloud"].includes(
    String(slug || "").trim().toLowerCase(),
  );
}

const GOOGLE_CREDIT_ONBOARDING_PROVIDER_PARAM = "credit_provider";
const GOOGLE_CREDIT_ONBOARDING_PASSIVE_ACTIONS = new Set([
  "",
  "none",
  "complete",
  "completed",
  "discovering",
  "monitor",
  "refresh",
  "sync",
  "syncing",
  "wait",
]);
const GOOGLE_CREDIT_BASELINE_ACTIONS = new Set([
  "add_baseline",
  "enter_baseline",
  "enter_console_balance",
  "enter_console_observation",
  "record_baseline",
  "record_console_balance",
]);

function safeProviderConnectionID(value?: string | null) {
  const providerID = String(value || "").trim();
  if (
    !providerID ||
    providerID.length > 256 ||
    /[\u0000-\u001f\u007f]/.test(providerID)
  ) {
    return "";
  }
  return providerID;
}

export function googleCreditOnboardingHref(providerID?: string | null) {
  const safeID = safeProviderConnectionID(providerID);
  const search = new URLSearchParams({ section: "providers" });
  if (safeID) search.set(GOOGLE_CREDIT_ONBOARDING_PROVIDER_PARAM, safeID);
  return `/settings?${search.toString()}#providers`;
}

export function readGoogleCreditOnboardingProviderID(search?: string | null) {
  const rawSearch =
    search ??
    (typeof window !== "undefined" ? window.location.search : "");
  try {
    return safeProviderConnectionID(
      new URLSearchParams(String(rawSearch || "").replace(/^\?/, "")).get(
        GOOGLE_CREDIT_ONBOARDING_PROVIDER_PARAM,
      ),
    );
  } catch {
    return "";
  }
}

export function googleCreditOnboardingRequiresInput(
  nextAction?: string | null,
) {
  const action = String(nextAction || "").trim().toLowerCase();
  return !GOOGLE_CREDIT_ONBOARDING_PASSIVE_ACTIONS.has(action);
}

export function googleCreditOnboardingNeedsBaseline(
  nextAction?: string | null,
  status?: ProviderCreditStatus | null,
) {
  const action = String(nextAction || "").trim().toLowerCase();
  return (
    GOOGLE_CREDIT_BASELINE_ACTIONS.has(action) ||
    (status?.kind === "applied_only" &&
      status.applied_promotion_micros !== null)
  );
}

export type GoogleCreditOnboardingControl =
  | "none"
  | "billing_account"
  | "dataset"
  | "export"
  | "source"
  | "permission"
  | "retry";

export function googleCreditOnboardingControl(
  state?: string | null,
  nextAction?: string | null,
): GoogleCreditOnboardingControl {
  const normalizedState = String(state || "").trim().toLowerCase();
  const action = String(nextAction || "").trim().toLowerCase();
  if (
    action === "set_billing_account" ||
    normalizedState === "needs_billing_account"
  ) {
    return "billing_account";
  }
  if (
    action === "enter_dataset_project" ||
    normalizedState === "needs_dataset_project"
  ) {
    return "dataset";
  }
  if (
    action === "configure_export" ||
    normalizedState === "needs_export"
  ) {
    return "export";
  }
  if (
    ["configure_source", "select_source"].includes(action) ||
    normalizedState === "source_choice_required"
  ) {
    return "source";
  }
  if (
    action === "grant_bigquery_access" ||
    normalizedState === "permission_required"
  ) {
    return "permission";
  }
  if (
    action === "retry" ||
    ["error", "unavailable"].includes(normalizedState)
  ) {
    return "retry";
  }
  return "none";
}

export function googleCreditCandidateSelection(
  candidate: {
    dataset_project_id?: string | null;
    dataset_id?: string | null;
  } | null | undefined,
) {
  const datasetProjectID = String(
    candidate?.dataset_project_id || "",
  ).trim();
  const datasetID = String(candidate?.dataset_id || "").trim();
  return datasetProjectID && datasetID
    ? {
        dataset_project_id: datasetProjectID,
        dataset_id: datasetID,
      }
    : null;
}

export function safeGoogleCreditsURL(value?: string | null) {
  try {
    const candidate = new URL(String(value || GOOGLE_CREDITS_URL));
    if (
      candidate.protocol === "https:" &&
      (candidate.hostname === "console.cloud.google.com" ||
        candidate.hostname.endsWith(".console.cloud.google.com"))
    ) {
      return candidate.toString();
    }
  } catch {
    // Fall through to the fixed Google Console page.
  }
  return GOOGLE_CREDITS_URL;
}

function formatDate(value?: string | null, locale?: string) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function sourceLabel(source?: string | null) {
  switch (String(source || "").trim().toLowerCase()) {
    case "gcp_bigquery_standard_export":
      return "Google billing export";
    case "gcp_cloud_billing_api":
      return "Google Cloud Billing API";
    default:
      return String(source || "").trim();
  }
}

function warningDetail(status: ProviderCreditStatus) {
  const codes = new Set(status.reason_codes || []);
  if (codes.has("GCP_BILLING_ACCOUNT_MISMATCH")) {
    return "The billing account on this Google provider connection does not match the configured credit source. Review the provider billing account, then reconnect credit visibility.";
  }
  if (codes.has("GCP_BILLING_EXPORT_FORBIDDEN")) {
    return "GENRL cannot read the billing export. Review the read-only BigQuery access.";
  }
  if (codes.has("GCP_BILLING_EXPORT_COVERAGE_INCOMPLETE")) {
    return "The export does not cover the full grant period.";
  }
  if (codes.has("GCP_CREDIT_GRANT_AMBIGUOUS")) {
    return "Applied Google promotional credits cannot yet be matched to one grant.";
  }
  if (
    codes.has("GCP_CREDIT_BASELINE_INCONSISTENT") ||
    codes.has("GCP_CREDIT_REBASELINE_REQUIRED")
  ) {
    return "The grant baseline must be checked before remaining credit can be estimated.";
  }
  if (codes.has("GCP_CREDIT_SOURCE_DISABLED")) {
    return "Google promotional credit visibility is disabled for this workspace.";
  }
  if (codes.has("GCP_CREDIT_DIRECT_TABLE_DISABLED")) {
    return "Direct-table access is disabled. Switch this source to the approved normalized view.";
  }
  return "";
}

export function providerCreditPresentation(
  status: ProviderCreditStatus,
  locale?: string,
): ProviderCreditPresentation {
  const remaining = formatProviderCreditMicros(
    status.remaining_amount_micros,
    status.currency,
    locale,
  );
  const applied = formatProviderCreditMicros(
    status.applied_promotion_micros,
    status.currency,
    locale,
  );
  const asOf = formatDate(
    status.as_of_at || status.data_through_export_at,
    locale,
  );
  const coverageStart = formatDate(status.coverage?.start_at, locale);
  const source = sourceLabel(status.source);
  const warning = warningDetail(status);
  const consoleURL = safeGoogleCreditsURL(status.console_url);
  const reasonCodes = new Set(status.reason_codes || []);
  const details = [
    source ? `Source: ${source}` : "",
    asOf ? `As of ${asOf}` : "",
    warning,
  ].filter(Boolean);

  if (reasonCodes.has("GCP_BILLING_ACCOUNT_MISMATCH")) {
    return {
      badge: "Billing account mismatch",
      tone: "danger",
      primary: "Google promotional credit balance needs attention",
      secondary:
        "The provider connection and credit source must use the same Google billing account.",
      details,
      action: "setup",
      consoleURL,
    };
  }

  if (status.data_state === "disabled") {
    return {
      badge: "Disabled",
      tone: "neutral",
      primary: "Google promotional credit visibility unavailable",
      secondary:
        "Google promotional credit visibility is not enabled for this workspace.",
      details,
      action: "google",
      consoleURL,
    };
  }

  if (status.data_state === "syncing" || status.data_state === "backfilling") {
    return {
      badge: status.data_state === "backfilling" ? "Backfilling" : "Syncing",
      tone: "info",
      primary: "Checking Google promotional credits…",
      secondary:
        status.data_state === "backfilling"
          ? "GENRL is reading the available export history."
          : "GENRL is refreshing applied Google promotional credits.",
      details,
      action: "details",
      consoleURL,
    };
  }

  if (status.data_state === "partial") {
    return {
      badge: "Partial history",
      tone: "warning",
      primary:
        status.applied_promotion_micros !== null
          ? `Google promotional credits applied${coverageStart ? ` since ${coverageStart}` : ""}: ${applied}`
          : "Google billing export history is incomplete",
      secondary:
        "Remaining Google promotional credit cannot be estimated from incomplete export history.",
      details,
      action: "details",
      consoleURL,
    };
  }

  if (
    status.kind === "estimated_remaining" &&
    status.remaining_amount_micros !== null
  ) {
    const stale =
      status.data_state === "stale" ||
      status.data_state === "delayed" ||
      status.data_state === "error";
    return {
      badge: stale
        ? status.data_state === "delayed"
          ? "Estimated · delayed"
          : "Estimated · stale"
        : "Estimated",
      tone: stale ? "warning" : "info",
      primary: `Estimated Google promotional credits remaining: ${remaining}`,
      secondary:
        status.applied_promotion_micros !== null
          ? `Based on ${applied} of Google promotional credits applied${asOf ? ` through ${asOf}` : ""}.`
          : `Estimated from the configured grant baseline${asOf ? ` as of ${asOf}` : ""}.`,
      details,
      action: stale ? "retry" : "details",
      consoleURL,
    };
  }

  if (
    status.kind === "authoritative_remaining" &&
    status.remaining_amount_micros !== null
  ) {
    const stale =
      status.data_state === "stale" ||
      status.data_state === "delayed" ||
      status.data_state === "error";
    return {
      badge: stale ? "Provider reported · stale" : "Provider reported",
      tone: stale ? "warning" : "positive",
      primary: `Google promotional credits remaining: ${remaining}`,
      secondary: `${source || "Google"}${asOf ? ` · updated ${asOf}` : ""}.`,
      details,
      action: stale ? "retry" : "details",
      consoleURL,
    };
  }

  if (
    status.kind === "applied_only" &&
    status.applied_promotion_micros !== null
  ) {
    const stale =
      status.data_state === "stale" ||
      status.data_state === "delayed" ||
      status.data_state === "error";
    return {
      badge: stale ? "Applied · stale" : "Applied only",
      tone: stale ? "warning" : "info",
      primary: `Google promotional credits applied: ${applied}`,
      secondary:
        "Remaining Google promotional credit is unavailable from Google.",
      details: [
        coverageStart ? `Export coverage starts ${coverageStart}` : "",
        ...details,
      ].filter(Boolean),
      action: stale ? "retry" : "details",
      consoleURL,
    };
  }

  if (status.data_state === "error") {
    return {
      badge: "Needs attention",
      tone: "danger",
      primary: "Google promotional credit visibility needs attention",
      secondary:
        warning ||
        "GENRL could not read a defensible Google promotional credit value.",
      details,
      action: "retry",
      consoleURL,
    };
  }

  return {
    badge: status.data_state === "not_configured" ? "Not configured" : "Unavailable",
    tone: "neutral",
    primary: "Google promotional credit balance unavailable",
    secondary:
      "Google does not expose remaining promotional credits through the Billing API.",
    details,
    action: status.data_state === "not_configured" ? "setup" : "google",
    consoleURL,
  };
}

export function providerCreditIsEstimated(
  status?: ProviderCreditStatus | null,
) {
  return (
    status?.kind === "estimated_remaining" &&
    status.remaining_amount_micros !== null
  );
}
